import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  ChevronDownRegular, 
  BookOpenRegular,
  SendRegular,
  SparkleRegular
} from '@fluentui/react-icons';
import Header from './Header';
import TemplateSelector from './TemplateSelector';
import ContactComposeView from './ContactComposeView';
import { ChatMessage } from './ChatMessage';
import { useLanguage } from '../contexts/LanguageContext';
import { useTextSize } from '../contexts/TextSizeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSocketConnectionWithACK, streamingApiClient, socketManager } from '../../donexus-frontend/endpoint/streaming';

// Generate a stable unique ID for chat messages
function generateMessageId() {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// ActionPromptBubbles component for compose suggestions
function ActionPromptBubbles({ actionPrompts, onPromptClick, className }) {
  const [clickedPromptId, setClickedPromptId] = useState(null);
  const timeoutRef = useRef(null);
  const { t } = useLanguage();
  const { currentTextSize } = useTextSize();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  if (!actionPrompts || actionPrompts.length === 0) {
    return null;
  }

  const handlePromptClick = (prompt) => {
    // Prevent multiple clicks on the same prompt
    if (clickedPromptId === prompt.id) {
      console.log('⏭️ Ignoring duplicate click on action prompt:', prompt.id);
      return;
    }
    
    // Clear any existing timeout before setting a new one
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    setClickedPromptId(prompt.id);
    onPromptClick(prompt);
    
    // Reset after a delay to allow re-clicking if needed
    timeoutRef.current = setTimeout(() => {
      setClickedPromptId(null);
      timeoutRef.current = null;
    }, 3000);
  };

  return (
    <div className={`mb-3 ${className || ''}`}>
      {/* Action Prompt Bubbles */}
      <div className="flex flex-col gap-1.5">
        {actionPrompts.map((prompt) => (
          <button
            key={prompt.id}
            onClick={() => handlePromptClick(prompt)}
            disabled={clickedPromptId === prompt.id}
            className={`
              group relative h-auto py-2 px-3 rounded-xl
              bg-white/90 backdrop-blur-sm
              border border-gray-200/80
              hover:bg-white hover:border-blue-300
              hover:shadow-sm
              transition-all duration-150 ease-in-out
              text-left justify-start
              max-w-[280px]
              ${clickedPromptId === prompt.id ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            title={prompt.question_for_details || prompt.action_prompt}
          >
            {/* Speech bubble tail */}
            <div className="absolute -bottom-1 left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-200/80 group-hover:border-t-blue-300 transition-colors" />
            
            <div className="flex items-center gap-2 w-full">
              {/* Icon */}
              <div className="flex-shrink-0">
                <div className="h-5 w-5 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
                  <SparkleRegular className="h-2.5 w-2.5 text-white" />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className={`${currentTextSize.bodyText} font-medium text-gray-700 group-hover:text-gray-900 leading-snug`}>
                  {prompt.display_action || prompt.action_prompt}
                </div>
              </div>

              {/* Arrow indicator */}
              <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <svg
                  className="h-3 w-3 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * MessageComposeView - Shown when the user is composing a new email
 * Features sections for Kira (AI Assistant), Ben (Research), Templates, and Contacts
 */
export default function MessageComposeView({ user, onLogout }) {
  const { t } = useLanguage();
  const { currentTextSize } = useTextSize();
  const { getAccessToken } = useAuth();
  
  // Track which section is open (only one at a time, like Dashboard)
  const [openSection, setOpenSection] = useState("ai");
  
  // AI Assistant state
  const [deepResearchEnabled, setDeepResearchEnabled] = useState(false); // false = Kira, true = Ben
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiSending, setAiSending] = useState(false);
  
  // Streaming state
  const [streamingResponse, setStreamingResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(null);
  const [chunkBuffer, setChunkBuffer] = useState(new Map());
  const [expectedFinalSequence, setExpectedFinalSequence] = useState(null);
  const [isStreamingComplete, setIsStreamingComplete] = useState(false);
  
  // Room and session management
  const [roomId, setRoomId] = useState(null);
  const [responseId, setResponseId] = useState(null);
  const [isRoomReady, setIsRoomReady] = useState(false);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  
  // Sources management
  const [streamingSources, setStreamingSources] = useState(new Map());
  const [currentStreamingSources, setCurrentStreamingSources] = useState([]);
  
  // Compose body monitoring for action prompts
  const [composeBodyText, setComposeBodyText] = useState('');
  const [showActionPrompts, setShowActionPrompts] = useState(false);
  const [actionPrompts, setActionPrompts] = useState([]);
  const composeBodyCheckIntervalRef = useRef(null);
  
  // Pending action prompt (stored after user clicks, sent with next message)
  const [pendingActionPrompt, setPendingActionPrompt] = useState(null);
  
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const streamingIntervalRef = useRef(null);

  // Initialize socket connection when user is authenticated
  useEffect(() => {
    if (user && !socketManager.isConnected()) {
      const token = getAccessToken();
      if (token) {
        console.log('🔌 MessageComposeView: Initializing socket connection');
        socketManager.connect(user, token);
      }
    }
  }, [user, getAccessToken]);

  // Cleanup streaming interval on unmount
  useEffect(() => {
    return () => {
      if (streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current);
        streamingIntervalRef.current = null;
      }
    };
  }, []);

  // Monitor compose body text and show action prompts when text > 50 characters
  useEffect(() => {
    const checkComposeBody = async () => {
      if (typeof Office === 'undefined' || !Office.context || !Office.context.mailbox) {
        return;
      }

      const mailbox = Office.context.mailbox;
      const item = mailbox.item;
      
      if (!item || item.itemType !== Office.MailboxEnums.ItemType.Message) {
        return;
      }

      try {
        // Get body content as text
        item.body.getAsync(Office.CoercionType.Text, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const bodyText = result.value || '';
            const trimmedText = bodyText.trim();
            
            setComposeBodyText(trimmedText);
            
            // Show action prompts if body text > 50 characters
            if (trimmedText.length > 50) {
              if (!showActionPrompts) {
                setShowActionPrompts(true);
                // Create action prompts with display_action, action_prompt, and question_for_details
                const prompts = [
                  {
                    id: 'compose_formal_german',
                    display_action: t('compose.actionPrompts.formalGerman.display'),
                    action_prompt: t('compose.actionPrompts.formalGerman.prompt'),
                    question_for_details: t('compose.actionPrompts.formalGerman.question')
                  },
                  {
                    id: 'compose_translate_english',
                    display_action: t('compose.actionPrompts.translateEnglish.display'),
                    action_prompt: t('compose.actionPrompts.translateEnglish.prompt'),
                    question_for_details: t('compose.actionPrompts.translateEnglish.question')
                  },
                  {
                    id: 'compose_spell_check',
                    display_action: t('compose.actionPrompts.spellCheck.display'),
                    action_prompt: t('compose.actionPrompts.spellCheck.prompt'),
                    question_for_details: t('compose.actionPrompts.spellCheck.question')
                  }
                ];
                console.log('✨ Creating action prompts with display_action, action_prompt, and question_for_details:', prompts);
                setActionPrompts(prompts);
              }
            } else {
              if (showActionPrompts) {
                setShowActionPrompts(false);
                setActionPrompts([]);
              }
            }
          }
        });
      } catch (error) {
        console.error('Error checking compose body:', error);
      }
    };

    // Check immediately
    checkComposeBody();

    // Set up interval to check periodically (every 2 seconds)
    composeBodyCheckIntervalRef.current = setInterval(checkComposeBody, 2000);

    // Cleanup interval on unmount
    return () => {
      if (composeBodyCheckIntervalRef.current) {
        clearInterval(composeBodyCheckIntervalRef.current);
      }
    };
  }, [showActionPrompts]);

  // Socket connection with ACK support
  const {
    socket,
    isConnected,
    connectionMetrics,
    setupMessageListener,
    joinRoom: socketJoinRoom,
    leaveRoom: socketLeaveRoom,
    createAndJoinSessionRoom,
    currentRoom
  } = useSocketConnectionWithACK();

  // Helper function to build response from buffer in sequence order
  const buildResponseFromBuffer = useCallback((buffer) => {
    if (buffer.size === 0) return '';
    
    const sequences = Array.from(buffer.keys()).sort((a, b) => a - b);
    const minSequence = sequences[0];
    
    let response = '';
    let currentSequence = minSequence;
    
    while (buffer.has(currentSequence)) {
      const chunk = buffer.get(currentSequence);
      if (chunk !== undefined) {
        response += chunk;
      }
      currentSequence++;
    }
    
    return response;
  }, []);

  // Check if all chunks are received
  const areAllChunksReceived = useCallback((buffer, finalSequence) => {
    if (finalSequence === null || buffer.size === 0) return false;
    
    const sequences = Array.from(buffer.keys()).sort((a, b) => a - b);
    const minSequence = sequences[0];
    
    for (let i = minSequence; i <= finalSequence; i++) {
      if (!buffer.has(i)) {
        return false;
      }
    }
    
    return true;
  }, []);

  // Finalize streaming completion
  const finalizeStreamingCompletion = useCallback((completionData) => {
    console.log('🏁 Finalizing streaming completion');
    
    setIsStreamingComplete(true);
    setIsStreaming(false);
    setCurrentProgress(null);
    
    if (completionData?.payload?.response_id) {
      setResponseId(completionData.payload.response_id);
    }
    
    setChunkBuffer(prevBuffer => {
      const finalResponse = buildResponseFromBuffer(prevBuffer);
      setStreamingResponse(finalResponse);
      
      if (finalResponse.trim()) {
        const aiMessage = {
          id: generateMessageId(),
          role: 'assistant',
          content: finalResponse,
          timestamp: new Date(),
          sources: currentStreamingSources
        };
        setAiMessages(prev => [...prev, aiMessage]);
        setCurrentStreamingSources([]);
      }
      
      return prevBuffer;
    });
    
    console.log('✅ Streaming completion finalized');
  }, [buildResponseFromBuffer, currentStreamingSources]);

  // Handle streaming chunk
  const handleStreamingChunk = useCallback((data) => {
    const messageRoomId = data.roomId;
    
    console.log('📦 Streaming chunk received - messageRoomId:', messageRoomId, 'currentRoomId:', roomId);
    
    if (!roomId || messageRoomId !== roomId) {
      console.warn('⚠️ Chunk ignored - room mismatch or no room. messageRoomId:', messageRoomId, 'currentRoomId:', roomId);
      return;
    }
    
    const payload = data.payload || data.data || data;
    const sequence = payload?.sequence;
    const chunkText = payload?.text || payload || '';
    
    console.log('✅ Processing chunk - sequence:', sequence, 'text length:', chunkText?.length);
    
    if (sequence !== undefined) {
      setIsStreaming(true);
      setCurrentProgress(null);
      
      setChunkBuffer(prevBuffer => {
        const newBuffer = new Map(prevBuffer);
        newBuffer.set(sequence, chunkText);
        
        const newResponse = buildResponseFromBuffer(newBuffer);
        setStreamingResponse(newResponse);
        
        return newBuffer;
      });
    }
  }, [roomId, buildResponseFromBuffer]);

  // Handle streaming complete
  const handleStreamingComplete = useCallback((data) => {
    const messageRoomId = data.roomId;
    
    if (!roomId || messageRoomId !== roomId) {
      return;
    }
    
    const payload = data.payload || data.data || data;
    const sequence = payload?.sequence;
    const finalChunkText = payload?.text || '';
    
    if (sequence !== undefined) {
      setExpectedFinalSequence(sequence);
    }
    
    if (sequence !== undefined && finalChunkText) {
      setChunkBuffer(prevBuffer => {
        const newBuffer = new Map(prevBuffer);
        newBuffer.set(sequence, finalChunkText);
        
        const finalResponse = buildResponseFromBuffer(newBuffer);
        setStreamingResponse(finalResponse);
        
        if (areAllChunksReceived(newBuffer, sequence)) {
          setTimeout(() => finalizeStreamingCompletion(data), 0);
        }
        
        return newBuffer;
      });
    } else {
      setTimeout(() => finalizeStreamingCompletion(data), 0);
    }
  }, [roomId, buildResponseFromBuffer, areAllChunksReceived, finalizeStreamingCompletion]);

  // Handle progress message
  const handleProgressMessage = useCallback((data) => {
    const messageRoomId = data.roomId;
    
    if (!roomId || messageRoomId !== roomId) {
      return;
    }
    
    const payload = data.payload || data.data || data;
    
    // If tool_name is present, translate it
    if (payload && payload.tool_name) {
      const translationKey = `incident.chat_agent.tools.${payload.tool_name}`;
      const progressText = t(translationKey, { defaultValue: payload.tool_name });
      setCurrentProgress(progressText);
    } else {
      // Otherwise use text or fallback to empty string
      const progressText = payload?.text || payload || '';
      setCurrentProgress(progressText);
    }
  }, [roomId, t]);

  // Handle streaming error
  const handleStreamingError = useCallback((data) => {
    const messageRoomId = data.roomId;
    
    if (!roomId || messageRoomId !== roomId) {
      return;
    }
    
    const payload = data.payload || data.data || data;
    const errorMessage = payload?.message || payload || 'An error occurred';
    
    console.error('❌ Streaming error:', errorMessage);
    
    setCurrentProgress(null);
    setIsStreaming(false);
    setStreamingResponse('');
    setChunkBuffer(new Map());
    setIsStreamingComplete(false);
    setCurrentStreamingSources([]);
    
    const errorMsg = {
      id: generateMessageId(),
      role: 'assistant',
      content: `❌ **Error**: ${errorMessage}`,
      timestamp: new Date()
    };
    setAiMessages(prev => [...prev, errorMsg]);
    
    alert(t('aiAssistant.errorOccurred') + ': ' + errorMessage);
  }, [roomId, t]);

  // Handle streaming sources
  const handleStreamingSources = useCallback((data) => {
    const messageRoomId = data.roomId;
    
    if (!roomId || messageRoomId !== roomId) {
      return;
    }
    
    const payload = data.payload || data.data || data;
    
    if (payload.id) {
      setStreamingSources(prev => {
        const updated = new Map(prev);
        updated.set(payload.id, payload);
        return updated;
      });
      
      setCurrentStreamingSources(prev => {
        const exists = prev.some(s => s.id === payload.id);
        if (exists) {
          return prev;
        }
        return [...prev, payload];
      });
    }
  }, [roomId]);

  // Handle citation clicks
  const handleCitationClick = useCallback((citationNumber, event, citationId, messageSources) => {
    event.stopPropagation();
    
    if (citationId && streamingSources.has(citationId)) {
      const source = streamingSources.get(citationId);
      
      if (source.embed_url) {
        window.open(source.embed_url, '_blank', 'noopener,noreferrer');
      } else {
        alert(`Quelle ${citationNumber}: ${source.original_filename || source.filepath || 'Quelleninformation'}`);
      }
      return;
    }
    
    const sourceIndex = citationNumber - 1;
    if (sourceIndex >= 0 && sourceIndex < messageSources.length) {
      const selectedSource = messageSources[sourceIndex];
      
      if (selectedSource.embed_url) {
        window.open(selectedSource.embed_url, '_blank', 'noopener,noreferrer');
      } else {
        alert(`Quelle ${citationNumber}: ${selectedSource.section_title || selectedSource.original_filename || 'Quelleninformation'}`);
      }
    }
  }, [streamingSources]);

  // Handle compose email - Fill the current compose window with AI-generated content
  const handleComposeEmail = useCallback(async (data) => {
    console.log('📧 Compose email received:', data);
    
    const payload = data.payload || data.data || data;
    const emailData = {
      subject: payload.subject || '',
      bodyHtml: payload.body_html || '',
      bodyText: payload.body_text || '',
      toAddresses: payload.to_addresses || [],
      ccAddresses: payload.cc_addresses || [],
      bccAddresses: payload.bcc_addresses || []
    };
    
    console.log('📧 Extracted emailData:', {
      hasBodyHtml: !!emailData.bodyHtml,
      hasBodyText: !!emailData.bodyText,
      bodyHtmlLength: emailData.bodyHtml?.length,
      bodyTextLength: emailData.bodyText?.length,
      subject: emailData.subject
    });

    // Helper to format recipients for Office.js
    const formatRecipients = (addresses) => {
      if (!addresses || !Array.isArray(addresses)) return [];
      return addresses.map(addr => {
        if (typeof addr === 'string') {
          return addr;
        }
        return addr.emailAddress || addr.address || addr;
      });
    };

    // Helper to improve HTML formatting for Outlook
    const formatHtmlForOutlook = (html) => {
      if (!html) return html;
      
      // Add margin to paragraph tags for better spacing in Outlook
      // Outlook often ignores external CSS, so we need inline styles
      let formattedHtml = html
        .replace(/<p>/g, '<p style="margin: 0 0 1em 0;">')
        .replace(/<p\s+style="([^"]*)"/g, '<p style="$1; margin: 0 0 1em 0;"');
      
      return formattedHtml;
    };

    // Promise-based helper with timeout for Office.js setAsync calls
    const setAsyncWithTimeout = (asyncFunction, options = {}, timeoutMs = 5000) => {
      return new Promise((resolve, reject) => {
        let timeoutId;
        let callbackInvoked = false;

        // Set up the timeout
        timeoutId = setTimeout(() => {
          if (!callbackInvoked) {
            callbackInvoked = true;
            reject(new Error('Office.js operation timed out after ' + timeoutMs + 'ms'));
          }
        }, timeoutMs);

        // Call the Office.js async function
        try {
          asyncFunction(options, (result) => {
            if (callbackInvoked) return; // Timeout already fired
            callbackInvoked = true;
            clearTimeout(timeoutId);

            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve(result);
            } else {
              reject(new Error(result.error?.message || 'Office.js operation failed'));
            }
          });
        } catch (error) {
          if (callbackInvoked) return;
          callbackInvoked = true;
          clearTimeout(timeoutId);
          reject(error);
        }
      });
    };

    try {
      // Check if Office.js is available
      if (typeof Office === 'undefined' || !Office.context || !Office.context.mailbox) {
        console.error('Office.js is not available');
        alert(t('aiAssistant.officeJsNotAvailable'));
        return;
      }

      // Log Office.CoercionType for debugging
      console.log('📋 Office.CoercionType.Html:', Office.CoercionType?.Html);
      console.log('📋 Office.CoercionType.Text:', Office.CoercionType?.Text);

      const mailbox = Office.context.mailbox;
      
      // Check if we're in compose mode (we should be!)
      if (mailbox.item && mailbox.item.itemType === Office.MailboxEnums.ItemType.Message) {
        const isCompose = mailbox.item.subject?.setAsync !== undefined;
        
        if (isCompose) {
          console.log('📝 Filling current compose window');
          
          const item = mailbox.item;
          
          try {
            // Set subject
            if (emailData.subject) {
              await setAsyncWithTimeout(
                (opts, callback) => item.subject.setAsync(emailData.subject, callback),
                {}
              );
              console.log('✅ Subject set:', emailData.subject);
            }
            
            // Set body (prefer HTML)
            let body = emailData.bodyHtml || emailData.bodyText;
            const coercionType = emailData.bodyHtml 
              ? Office.CoercionType.Html 
              : Office.CoercionType.Text;
            
            // Format HTML for better display in Outlook
            if (emailData.bodyHtml && body) {
              body = formatHtmlForOutlook(body);
            }
            
            if (body) {
              console.log('📝 Setting body with coercionType:', coercionType, 'bodyHtml present:', !!emailData.bodyHtml, 'bodyText present:', !!emailData.bodyText);
              console.log('📝 Body content (first 200 chars):', body.substring(0, 200));
              
              await setAsyncWithTimeout(
                (opts, callback) => item.body.setAsync(body, { coercionType, ...opts }, callback),
                {}
              );
              console.log('✅ Body set successfully with coercionType:', coercionType);
            }
            
            // Set recipients
            const toRecipients = formatRecipients(emailData.toAddresses);
            if (toRecipients.length > 0) {
              await setAsyncWithTimeout(
                (opts, callback) => item.to.setAsync(toRecipients, callback),
                {}
              );
              console.log('✅ To recipients set:', toRecipients);
            }
            
            const ccRecipients = formatRecipients(emailData.ccAddresses);
            if (ccRecipients.length > 0) {
              await setAsyncWithTimeout(
                (opts, callback) => item.cc.setAsync(ccRecipients, callback),
                {}
              );
              console.log('✅ CC recipients set:', ccRecipients);
            }
            
            const bccRecipients = formatRecipients(emailData.bccAddresses);
            if (bccRecipients.length > 0) {
              await setAsyncWithTimeout(
                (opts, callback) => item.bcc.setAsync(bccRecipients, callback),
                {}
              );
              console.log('✅ BCC recipients set:', bccRecipients);
            }
          } catch (error) {
            console.error('❌ Failed to compose email:', error);
            alert(`${t('aiAssistant.emailComposeFailed')} ${error.message}`);
            return;
          }
          
          console.log('✅ Compose window filled successfully');
          
          // Add a success message to the chat
          const successMessage = {
            id: generateMessageId(),
            role: 'assistant',
            content: `✅ ${t('aiAssistant.emailDraftPrepared')}\n\n**${t('aiAssistant.subject')}:** ${emailData.subject || '(no subject)'}\n**To:** ${toRecipients.join(', ') || '(none)'}${ccRecipients.length > 0 ? `\n**CC:** ${ccRecipients.join(', ')}` : ''}`,
            timestamp: new Date()
          };
          setAiMessages(prev => [...prev, successMessage]);
          
          return;
        }
      }
      
      // If we're somehow not in compose mode, show an error
      console.error('Not in compose mode - this should not happen in MessageComposeView');
      alert(t('aiAssistant.notInComposeMode') || 'Not in compose mode');
      
    } catch (error) {
      console.error('Error composing email:', error);
      alert(t('aiAssistant.emailComposeFailed') + ': ' + error.message);
      
      const errorMsg = {
        id: generateMessageId(),
        role: 'assistant',
        content: `❌ ${t('aiAssistant.emailComposeFailed')}`,
        timestamp: new Date()
      };
      setAiMessages(prev => [...prev, errorMsg]);
    }
  }, [t]);

  // Setup message listeners
  useEffect(() => {
    if (!isConnected) {
      console.log('⚠️ MessageComposeView: Not connected, skipping message listener setup');
      return;
    }

    console.log('🎧 MessageComposeView: Setting up message listeners for roomId:', roomId);

    const handleMessage = (data) => {
      console.log('📨 MessageComposeView: Message received - type:', data.type, 'roomId:', data.roomId);
      
      switch (data.type) {
        case 'streaming_chunk':
          handleStreamingChunk(data);
          break;
        case 'streaming_complete':
        case 'streaming_end':
          handleStreamingComplete(data);
          break;
        case 'streaming_progress':
          handleProgressMessage(data);
          break;
        case 'streaming_error':
          handleStreamingError(data);
          break;
        case 'streaming_sources':
          handleStreamingSources(data);
          break;
        case 'compose_email':
          handleComposeEmail(data);
          break;
        default:
          console.log('⚠️ Unknown message type:', data.type);
          break;
      }
    };

    const cleanup = setupMessageListener(handleMessage, [
      'streaming_chunk',
      'streaming_complete',
      'streaming_end',
      'streaming_progress',
      'streaming_error',
      'streaming_sources',
      'compose_email'
    ]);

    console.log('✅ MessageComposeView: Message listeners set up successfully');

    return () => {
      console.log('🧹 MessageComposeView: Cleaning up message listeners');
      cleanup();
    };
  }, [
    isConnected,
    roomId,
    setupMessageListener,
    handleStreamingChunk,
    handleStreamingComplete,
    handleProgressMessage,
    handleStreamingError,
    handleStreamingSources,
    handleComposeEmail
  ]);

  // Create room on mount
  useEffect(() => {
    const initializeRoom = async () => {
      console.log('🔍 MessageComposeView: Room initialization check - isConnected:', isConnected, 'roomId:', roomId);
      
      if (isConnected && !roomId) {
        try {
          console.log('🏠 MessageComposeView: Creating compose session room...');
          
          const metadata = {
            embedded: true,
            context: 'outlook-compose',
            created_at: new Date().toISOString(),
            request_id: `outlook-compose-chat-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`
          };
          
          console.log('📋 MessageComposeView: Room metadata:', metadata);
          
          const newRoomId = await createAndJoinSessionRoom('outlook-compose-chat', metadata);
          
          console.log('✅ MessageComposeView: Room created and joined:', newRoomId);
          
          setRoomId(newRoomId);
          setIsRoomReady(true);
          
          console.log('✅ MessageComposeView: State updated - roomId:', newRoomId, 'isRoomReady: true');
        } catch (error) {
          console.error('❌ MessageComposeView: Failed to create room:', error);
        }
      } else if (!isConnected) {
        console.log('⏳ MessageComposeView: Waiting for connection...');
      } else if (roomId) {
        console.log('ℹ️ MessageComposeView: Room already exists:', roomId);
      }
    };

    initializeRoom();
  }, [isConnected, roomId, createAndJoinSessionRoom]);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll when conversation updates
  useEffect(() => {
    if (aiMessages.length > 0 || streamingResponse) {
      scrollToBottom();
    }
  }, [aiMessages, streamingResponse]);

  // Toggle between Kira and Ben
  const handleResearchToggle = () => {
    setDeepResearchEnabled(prev => !prev);
  };

  // Ensure a room exists and is joined
  const ensureRoomReady = useCallback(async () => {
    if (roomId && isRoomReady) return roomId;
    if (!isConnected) return null;
    if (isCreatingRoom) return null;

    try {
      setIsCreatingRoom(true);
      const metadata = {
        embedded: true,
        context: 'outlook-compose',
        created_at: new Date().toISOString(),
        request_id: `outlook-compose-chat-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`
      };
      const newRoomId = await createAndJoinSessionRoom('outlook-compose-chat', metadata);
      setRoomId(newRoomId);
      setIsRoomReady(true);
      return newRoomId;
    } catch (error) {
      console.error('Failed to ensure room ready:', error);
      return null;
    } finally {
      setIsCreatingRoom(false);
    }
  }, [roomId, isRoomReady, isConnected, isCreatingRoom, createAndJoinSessionRoom]);

  // Get current compose window content (subject, body, recipients)
  const getComposeContext = useCallback(async () => {
    if (typeof Office === 'undefined' || !Office.context || !Office.context.mailbox) {
      return null;
    }

    const mailbox = Office.context.mailbox;
    const item = mailbox.item;
    
    if (!item || item.itemType !== Office.MailboxEnums.ItemType.Message) {
      return null;
    }

    try {
      // Get subject
      const subject = await new Promise((resolve) => {
        item.subject.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value || '');
          } else {
            console.warn('Failed to get subject:', result.error);
            resolve('');
          }
        });
      });

      // Get body content (as text)
      const body = await new Promise((resolve) => {
        item.body.getAsync(Office.CoercionType.Text, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value || '');
          } else {
            console.warn('Failed to get body:', result.error);
            resolve('');
          }
        });
      });

      // Get To recipients
      const toRecipients = await new Promise((resolve) => {
        item.to.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const recipients = result.value.map(r => r.emailAddress || r.displayName || '');
            resolve(recipients.filter(r => r));
          } else {
            console.warn('Failed to get to recipients:', result.error);
            resolve([]);
          }
        });
      });

      // Get CC recipients
      const ccRecipients = await new Promise((resolve) => {
        item.cc.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const recipients = result.value.map(r => r.emailAddress || r.displayName || '');
            resolve(recipients.filter(r => r));
          } else {
            console.warn('Failed to get cc recipients:', result.error);
            resolve([]);
          }
        });
      });

      return {
        subject: subject || '',
        body: body || '',
        to_recipients: toRecipients || [],
        cc_recipients: ccRecipients || []
      };
    } catch (error) {
      console.error('Error getting compose context:', error);
      return null;
    }
  }, []);

  // Handle action prompt click - show action_prompt in chat, then ask question_for_details
  const handleActionPromptClick = async (prompt) => {
    console.log('🎯 Action prompt clicked:', prompt);
    console.log('📝 Full prompt object:', JSON.stringify(prompt, null, 2));
    
    // Open the AI section if it's not already open
    setOpenSection("ai");
    
    // Add user message to conversation (show the action_prompt as user message)
    const userMessage = {
      id: generateMessageId(),
      role: 'user',
      content: prompt.action_prompt,
      timestamp: new Date()
    };
    setAiMessages(prev => [...prev, userMessage]);

    // If there's a question_for_details, show it as assistant message with streaming effect
    if (prompt.question_for_details) {
      setTimeout(() => {
        const fullQuestion = prompt.question_for_details;
        const questionTimestamp = new Date();
        
        // Create a unique ID for this streaming message
        const streamingMessageId = `streaming-${Date.now()}`;
        
        // Add initial empty assistant message
        const assistantQuestion = {
          id: streamingMessageId,
          role: 'assistant',
          content: '',
          timestamp: questionTimestamp,
          isStreaming: true,
          streamingId: streamingMessageId
        };
        setAiMessages(prev => [...prev, assistantQuestion]);
        
        // Stream the question character by character
        let currentIndex = 0;
        const chunkSize = 4; // Characters per chunk (adjust for speed)
        const intervalDelay = 15; // ms between chunks (adjust for speed)
        
        const streamInterval = setInterval(() => {
          if (currentIndex < fullQuestion.length) {
            const nextChunk = fullQuestion.slice(0, currentIndex + chunkSize);
            currentIndex += chunkSize;
            
            // Update the streaming message
            setAiMessages(prev => prev.map(msg => 
              msg.streamingId === streamingMessageId
                ? { ...msg, content: nextChunk }
                : msg
            ));
          } else {
            // Streaming complete
            clearInterval(streamInterval);
            
            // Finalize the message (remove streaming flag)
            setAiMessages(prev => prev.map(msg => 
              msg.streamingId === streamingMessageId
                ? { ...msg, content: fullQuestion, isStreaming: false }
                : msg
            ));
            
            // Store the action prompt for the next message
            setPendingActionPrompt(prompt);
            
            // Focus textarea for user to respond
            textareaRef.current?.focus();
          }
        }, intervalDelay);
      }, 500); // Initial delay of 500ms (0.5 seconds)
    } else {
      // No follow-up question - send immediately with compose context
      await sendActionPromptMessage(prompt);
    }
  };

  // Send action prompt message to AI with compose context
  const sendActionPromptMessage = async (prompt) => {
    // Ensure we have a room ready and get the current room ID
    let currentRoomId = roomId;
    if (!currentRoomId || !isRoomReady) {
      const ensuredRoomId = await ensureRoomReady();
      if (!ensuredRoomId) {
        console.error('Failed to ensure room ready');
        return;
      }
      currentRoomId = ensuredRoomId;
    }

    setAiSending(true);

    // Reset streaming state
    setStreamingResponse('');
    setChunkBuffer(new Map());
    setIsStreamingComplete(false);
    setExpectedFinalSequence(null);
    setCurrentProgress(t('aiAssistant.analyzingMessage'));

    try {
      // Get current compose window content
      const composeContext = await getComposeContext();
      
      const chatPayload = {
        type: 'chat_message',
        roomId: currentRoomId,
        payload: {
          message: prompt.action_prompt,
          response_id: responseId,
          outlook_addin: true,
          compose_context: true,
          settings: {
            web_search: false,
            deep_research: deepResearchEnabled
          },
          compose_mail: {
            current_text: composeContext?.body || '',
            current_subject: composeContext?.subject || '',
            current_recipients: {
              to: composeContext?.to_recipients || [],
              cc: composeContext?.cc_recipients || []
            }
          }
        },
        timestamp: new Date().toISOString(),
        data: {
          source: 'outlook_compose_chat',
          room_id: currentRoomId
        }
      };

      console.log('📤 Sending action prompt message with roomId:', currentRoomId);
      console.log('📦 Full payload being sent:', JSON.stringify(chatPayload, null, 2));

      const response = await streamingApiClient.processMessage(chatPayload);

      if (response && response.success !== false) {
        console.log('✅ Action prompt message sent successfully');
        
        if (response.response_id) {
          setResponseId(response.response_id);
        }
      } else {
        throw new Error(response?.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending action prompt message:', error);
      
      const errorMsg = {
        id: generateMessageId(),
        role: 'assistant',
        content: t('aiAssistant.errorOccurred'),
        timestamp: new Date()
      };
      setAiMessages(prev => [...prev, errorMsg]);
      
      alert(t('aiAssistant.errorOccurred') + ': ' + error.message);
    } finally {
      setAiSending(false);
    }
  };

  // Reset chat - leave old room and join new one
  const handleResetChat = async () => {
    // Leave the old room if it exists
    const oldRoomId = roomId;
    if (oldRoomId) {
      console.log('🚪 Leaving old room:', oldRoomId);
      socketLeaveRoom(oldRoomId);
    }
    
    // Reset all state
    setAiMessages([]);
    setAiInput('');
    setAiSending(false);
    setStreamingResponse('');
    setChunkBuffer(new Map());
    setIsStreamingComplete(false);
    setExpectedFinalSequence(null);
    setIsStreaming(false);
    setCurrentProgress(null);
    setResponseId(null);
    setRoomId(null);
    setIsRoomReady(false);
    setDeepResearchEnabled(false);
    setStreamingSources(new Map());
    setCurrentStreamingSources([]);
    setPendingActionPrompt(null);
    
    // Create and join a new room
    if (isConnected) {
      try {
        console.log('🏠 Creating new room after reset...');
        
        const metadata = {
          embedded: true,
          context: 'outlook-compose',
          created_at: new Date().toISOString(),
          request_id: `outlook-compose-chat-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`
        };
        
        const newRoomId = await createAndJoinSessionRoom('outlook-compose-chat', metadata);
        setRoomId(newRoomId);
        setIsRoomReady(true);
        console.log('✅ New room created after reset:', newRoomId, 'with metadata:', metadata);
      } catch (error) {
        console.error('Failed to create new room after reset:', error);
      }
    }
  };

  // Handle AI message send (works for both Kira and Ben)
  const handleAiSend = async () => {
    if (!aiInput.trim() || aiSending) {
      return;
    }
    
    // Ensure we have a room ready and get the current room ID
    let currentRoomId = roomId;
    if (!currentRoomId || !isRoomReady) {
      const ensuredRoomId = await ensureRoomReady();
      if (!ensuredRoomId) {
        console.error('Failed to ensure room ready');
        return;
      }
      currentRoomId = ensuredRoomId;
    }

    const userMessageContent = aiInput;
    const actionPromptToSend = pendingActionPrompt;
    
    setAiInput('');
    setAiSending(true);

    // Reset streaming state
    setStreamingResponse('');
    setChunkBuffer(new Map());
    setIsStreamingComplete(false);
    setExpectedFinalSequence(null);
    setCurrentProgress(t('aiAssistant.analyzingMessage'));

    // Add user message to conversation
    const userMessage = {
      id: generateMessageId(),
      role: 'user',
      content: userMessageContent,
      timestamp: new Date()
    };
    setAiMessages(prev => [...prev, userMessage]);

    try {
      // Get current compose window content
      const composeContext = await getComposeContext();
      
      const chatPayload = {
        type: 'chat_message',
        roomId: currentRoomId,
        payload: {
          message: userMessageContent,
          response_id: responseId,
          outlook_addin: true,
          compose_context: true, // Indicate this is compose context
          settings: {
            web_search: false,
            deep_research: deepResearchEnabled
          },
          compose_mail: {
            current_text: composeContext?.body || '',
            current_subject: composeContext?.subject || '',
            current_recipients: {
              to: composeContext?.to_recipients || [],
              cc: composeContext?.cc_recipients || []
            }
          },
          // Include action prompt context if available
          ...(actionPromptToSend && {
            action_prompt: {
              id: actionPromptToSend.id,
              display_action: actionPromptToSend.display_action,
              action_prompt: actionPromptToSend.action_prompt,
              question_for_details: actionPromptToSend.question_for_details
            }
          })
        },
        timestamp: new Date().toISOString(),
        data: {
          source: 'outlook_compose_chat',
          room_id: currentRoomId
        }
      };

      // Clear pending action prompt
      if (actionPromptToSend) {
        setPendingActionPrompt(null);
        console.log('✅ Action prompt sent with message');
      }

      console.log('📤 Sending compose message with roomId:', currentRoomId, 'payload:', chatPayload);

      const response = await streamingApiClient.processMessage(chatPayload);

      if (response && response.success !== false) {
        console.log('✅ Message sent successfully');
        
        if (response.response_id) {
          setResponseId(response.response_id);
        }
      } else {
        throw new Error(response?.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMsg = {
        id: generateMessageId(),
        role: 'assistant',
        content: t('aiAssistant.errorOccurred'),
        timestamp: new Date()
      };
      setAiMessages(prev => [...prev, errorMsg]);
      
      alert(t('aiAssistant.errorOccurred') + ': ' + error.message);
    } finally {
      setAiSending(false);
    }
  };

  return (
    <div className="bg-white min-h-screen font-sans flex flex-col">
      <Header 
        user={user} 
        onLogout={onLogout}
        onResetChat={handleResetChat}
        showResetChat={aiMessages.length > 0}
      />

      {/* Expandable Sections */}
      <div className="flex-1 overflow-y-auto">
        {/* AI Assistant Section (Kira/Ben Toggle) */}
        <div className="border-b border-gray-300">
          <button 
            className={`flex items-center justify-between w-full ${currentTextSize.cardPadding} hover:bg-gray-50 transition-colors`}
            onClick={() => setOpenSection(openSection === "ai" ? null : "ai")}
          >
            <div className="flex items-center gap-3">
              <img 
                src={deepResearchEnabled ? "assets/Ben.png" : "assets/Kira.png"}
                alt={deepResearchEnabled ? t('aiAssistant.benName') : t('aiAssistant.kiraName')}
                className="h-10 w-10 rounded-full"
              />
              <div className="text-left">
                <h3 className={`${currentTextSize.headerText} font-semibold text-gray-900`}>
                  {deepResearchEnabled ? t('aiAssistant.benName') : t('aiAssistant.kiraName')}
                </h3>
                <p className={`${currentTextSize.caption} text-gray-500`}>
                  {deepResearchEnabled ? t('aiAssistant.benSubtitle') : t('aiAssistant.kiraSubtitle')}
                </p>
              </div>
            </div>
            <ChevronDownRegular
              className={`h-4 w-4 text-gray-500 transition-transform ${
                openSection === "ai" ? "rotate-180" : ""
              }`}
            />
          </button>

          {openSection === "ai" && (
            <div className={`${currentTextSize.spacing}`}>
              {/* Action Prompt Bubbles - show when body text > 50 chars and no conversation yet */}
              {showActionPrompts && aiMessages.length === 0 && (
                <div className="px-4 pt-4">
                  <ActionPromptBubbles 
                    actionPrompts={actionPrompts}
                    onPromptClick={handleActionPromptClick}
                  />
                </div>
              )}

              {/* Messages Container */}
              {aiMessages.length > 0 && (
                <div className="max-h-96 overflow-y-auto px-4 pt-4 space-y-4">
                  {aiMessages.map((msg, index) => (
                    <ChatMessage 
                      key={
                        msg.id ||
                        msg.streamingId ||
                        `${msg.role}-${typeof msg.timestamp === 'string' ? Date.parse(msg.timestamp) : (msg.timestamp?.getTime?.() || index)}`
                      }
                      message={msg}
                      textSizeClasses={currentTextSize}
                      onCitationClick={handleCitationClick}
                      streamingSources={streamingSources}
                      deepResearchEnabled={deepResearchEnabled}
                    />
                  ))}
                  
                  {/* Progress Indicator */}
                  {currentProgress && !isStreaming && (
                    <div className="flex justify-start">
                      <div className={`${currentTextSize.messageText} text-gray-500 italic`}>
                        {currentProgress}
                        <span className="ml-1 animate-pulse">...</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Streaming Response */}
                  {isStreaming && streamingResponse && (
                    <ChatMessage 
                      message={{
                        role: 'assistant',
                        content: streamingResponse,
                        timestamp: new Date(),
                        sources: currentStreamingSources
                      }}
                      isStreaming={true}
                      textSizeClasses={currentTextSize}
                      onCitationClick={handleCitationClick}
                      streamingSources={streamingSources}
                      deepResearchEnabled={deepResearchEnabled}
                    />
                  )}
                  
                  {/* Typing Indicator */}
                  {isStreaming && !streamingResponse && (
                    <div className="flex justify-start mb-4">
                      <div className="flex gap-3 max-w-[80%]">
                        <div className={`flex-shrink-0 ${currentTextSize.avatarSize} rounded-full bg-gray-100 flex items-center justify-center`}>
                          <img 
                            src={deepResearchEnabled ? "assets/Ben.png" : "assets/Kira.png"}
                            alt={deepResearchEnabled ? t('aiAssistant.benName') : t('aiAssistant.kiraName')}
                            className="h-full w-full rounded-full object-cover"
                          />
                        </div>
                        <div className={`bg-gray-100 rounded-lg ${currentTextSize.padding}`}>
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              )}

              {/* Message Input */}
              <div className="px-4 pb-4 pt-2 space-y-2">
                <textarea 
                  ref={textareaRef}
                  placeholder={t('aiAssistant.messagePlaceholder')}
                  className={`w-full ${currentTextSize.minHeight} ${currentTextSize.padding} ${currentTextSize.inputText} border border-gray-300 rounded-md resize-y focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleAiSend()}
                  disabled={aiSending || !isRoomReady}
                />

                {/* Connection status */}
                {!isConnected && (
                  <div className={`${currentTextSize.buttonText} text-amber-600 bg-amber-50 p-2 rounded`}>
                    {t('aiAssistant.connecting')}...
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    {/* Research Toggle Button */}
                    <button 
                      onClick={handleResearchToggle}
                      className={`h-8 w-8 p-0 rounded flex items-center justify-center transition-colors ${
                        deepResearchEnabled 
                          ? 'bg-blue-600 text-white hover:bg-blue-700' 
                          : 'text-blue-600 hover:bg-blue-50'
                      }`}
                      title={t('aiAssistant.research')}
                    >
                      <BookOpenRegular className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Send Button */}
                  <button
                    onClick={handleAiSend}
                    disabled={!aiInput.trim() || aiSending || !isRoomReady}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                    title={!isRoomReady ? t('aiAssistant.connectingToRoom') : ''}
                  >
                    {aiSending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <SendRegular className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Templates Section */}
        <TemplateSelector 
          isOpen={openSection === "templates"}
          onToggle={() => setOpenSection(openSection === "templates" ? null : "templates")}
        />

        {/* Contacts Section */}
        <ContactComposeView
          isOpen={openSection === "contacts"}
          onToggle={() => setOpenSection(openSection === "contacts" ? null : "contacts")}
        />
      </div>
    </div>
  );
}

